import React, { useState, useEffect } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from "react-hook-form";
import Select from "react-select";
import { showToast } from "../../toasts";
import BlockedDriversTable from "../BlockedDriversTable";

const FleetAndDrivers = ({
    selectedCompany,
    fetchCompany,
    carTypeOptions = [],
}) => {
    const {
        register,
        watch,
        setValue,
        reset,
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);
    const [fleetOptions, setFleetOptions] = useState([]);
    const [fleets, merchant_fleets, allowed_inbound_car_types, allowed_outbound_car_types] =
        watch(['fleets', 'merchant_fleets', 'allowed_inbound_car_types', 'allowed_outbound_car_types']);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [selectedReason, setSelectedReason] = useState(null);
    const [resetSelection, setResetSelection] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const [newDriverRows, setNewDriverRows] = useState([]);

    useEffect(() => {
        register('fleets');
        register('merchant_fleets');
        register('newDriverRows');
        register('allowed_inbound_car_types');
        register('allowed_outbound_car_types');
    }, []);

    const fetchFleets = async () => {
        try {
            const fleetsResponse = await axiosInstance.get('/fleets.json');
            const allFleets = fleetsResponse.data.map(fleet => ({
                value: fleet.id,
                label: fleet.name
            }));
            setFleetOptions(allFleets);
        }
        catch (error) {
            console.error("Error fetching fleets:", error);
        }
    };

    useEffect(() => {
        fetchFleets();
    }, []);

    const handleMerchantFleetsChange = (e) => {
        const newValue = e.target.checked;
        setValue('merchant_fleets', newValue, { shouldDirty: true });
    };

    const handleFleetChange = (newValue) => {
        setValue('fleets', newValue, { shouldDirty: true });
    };

    // Keep the local pending-rows state and the react-hook-form value in sync
    // (the form copy drives the unsaved-changes guard and revert).
    const updateNewDriverRows = (rows) => {
        setNewDriverRows(rows);
        setValue('newDriverRows', rows);
    };

    const updateCompany = async () => {
        setIsLoading(true);
        try {
            // Update fleets
            await axiosInstance.patch(`/companies/${selectedCompany.id}.json`, {
                company: {
                    fleet_ids: fleets.map(fleet => fleet.value),
                    merchant_fleets,
                    allowed_inbound_car_types: allowed_inbound_car_types ?? [],
                    allowed_outbound_car_types: allowed_outbound_car_types ?? []
                }
            });

            // Save all new driver deactivations in parallel. Promise.allSettled
            // is used so one failing row (for example a driver that was just
            // deactivated elsewhere) does not abort the rest, and so partial
            // failures can be reported back to the user.
            if (newDriverRows.length > 0) {
                const results = await Promise.allSettled(
                    newDriverRows.map(row =>
                        axiosInstance.post('/user_company_deactivations.json', {
                            user_company_deactivation: {
                                user_id: row.user_id,
                                reason: row.reason
                            },
                            company_id: selectedCompany.id
                        })
                    )
                );

                const succeededCount = results.filter(result => result.status === 'fulfilled').length;
                // Keep any rows that failed so the user can see and retry them.
                const failedRows = newDriverRows.filter((_, index) => results[index].status === 'rejected');

                if (succeededCount > 0) {
                    showToast('success', `Saved. ${succeededCount} driver(s) blocked.`);
                }
                if (failedRows.length > 0) {
                    showToast('error', `${failedRows.length} driver(s) could not be blocked. Please try again.`);
                }

                updateNewDriverRows(failedRows);
                setResetSelection(prev => prev + 1);
                setRefreshKey(prev => prev + 1);
            } else {
                showToast('success', 'Company settings saved.');
            }

            await fetchCompany();

        } catch (error) {
            console.error("Error updating company:", error);
            showToast('error', 'Failed to save company settings. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRevertChanges = async () => {
        reset();
        updateNewDriverRows([]);
    };

    return (
        <div className="w-full p-4 flex flex-col">
            {/* Assigned Fleets section */}
            <div className="flex flex-wrap px-3 md:px-0">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Assigned Fleets</h3>
                        <p className="text-sm text-gray-500">Fleet settings</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">
                    <div className="w-full flex flex-wrap">
                        <div className="w-full md:w-1/6 flex flex-col">
                            {isLoading ? (
                                <div className="animate-pulse flex items-center">
                                    <div className="h-5 w-5 bg-gray-200 rounded mr-2"></div>
                                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                                </div>
                            ) : (
                                <div>
                                    <label className="label font-medium text-gray-900" style={{ whiteSpace: 'pre-wrap' }}>3rd Party Fleets</label>
                                    <div>
                                        <input
                                            type="checkbox"
                                            className="form-checkbox"
                                            checked={merchant_fleets || false}
                                            style={{
                                                transform: 'scale(3)',
                                                width: '15px',
                                                height: '15px',
                                                padding: '0',
                                                margin: '0' ,
                                                marginLeft: '15px',
                                                marginTop: '10px'
                                            }}
                                            onChange={(e) => handleMerchantFleetsChange(e)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="w-full md:w-5/6 flex flex-col">
                            <label className="block text-sm font-medium text-gray-700">
                                Multi select fleets
                            </label>
                            {isLoading ? (
                                <div className="animate-pulse mt-1">
                                    <div className="h-10 bg-gray-200 rounded"></div>
                                </div>
                            ) : (
                                <Select
                                    placeholder={"Multi select fleets"}
                                    closeMenuOnSelect={false}
                                    isMulti
                                    options={fleetOptions}
                                    value={fleets}
                                    onChange={(value) => handleFleetChange(value)}
                                    className="mt-1"
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-5 " />

            {/* Blocked Drivers section */}
            <div className="flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Blocked Drivers</h3>
                        <p className="text-sm text-gray-500">Blocked drivers will be prevented from being assigned to trips for this company.</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4">
                    <BlockedDriversTable
                        selectedCompany={selectedCompany}
                        selectedDriver={selectedDriver}
                        setSelectedDriver={setSelectedDriver}
                        selectedReason={selectedReason}
                        setSelectedReason={setSelectedReason}
                        resetSelection={resetSelection}
                        refreshKey={refreshKey}
                        onNewRowsChange={updateNewDriverRows}
                        newRows={newDriverRows}
                        fleetOptions={fleetOptions}
                    />
                </div>
            </div>

            <hr className="border border-gray-200 my-5 " />

            {/* Allowed Vehicle Types (GMPRO) section */}
            <div className="flex flex-wrap px-3 md:px-0">
                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">Allowed Vehicle Types (GMPRO)</h3>
                        <p className="text-sm text-gray-500">Restrict which vehicle types GMPRO may use per direction. Leave a direction empty to allow all types.</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-wrap">
                    <div className="w-full md:w-1/2 flex flex-col md:pr-3 mb-4 md:mb-0" data-testid="allowed-inbound-car-types">
                        <label className="block text-sm font-medium text-gray-700">Inbound</label>
                        <Select
                            placeholder={"All vehicle types allowed"}
                            isMulti
                            closeMenuOnSelect={false}
                            options={carTypeOptions}
                            value={carTypeOptions.filter(option => (allowed_inbound_car_types ?? []).includes(option.value))}
                            onChange={(selected) =>
                                setValue('allowed_inbound_car_types', (selected ?? []).map(option => option.value), { shouldDirty: true })
                            }
                            className="mt-1"
                        />
                    </div>
                    <div className="w-full md:w-1/2 flex flex-col md:pl-3" data-testid="allowed-outbound-car-types">
                        <label className="block text-sm font-medium text-gray-700">Outbound</label>
                        <Select
                            placeholder={"All vehicle types allowed"}
                            isMulti
                            closeMenuOnSelect={false}
                            options={carTypeOptions}
                            value={carTypeOptions.filter(option => (allowed_outbound_car_types ?? []).includes(option.value))}
                            onChange={(selected) =>
                                setValue('allowed_outbound_car_types', (selected ?? []).map(option => option.value), { shouldDirty: true })
                            }
                            className="mt-1"
                        />
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={()=> updateCompany()}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        className='p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer bg-gray-500 hover:bg-gray-600'
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => handleRevertChanges()}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FleetAndDrivers;